import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Menu, Row, Col, Space, Typography, theme, Tag } from "antd";
import { DownOutlined, LeftOutlined, UpOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOpHistoryAPI } from "../../services/physio.services";
import { getuserRoleTabs } from "./OPLandingLayout/opTabs";
import { getCookie } from "../../helpers/localStorage";
import { Role } from "../../constants/defaultKeys";
import { UiTag } from "../ui";
import { formatPatientAge } from "../../helpers/formatAge";
import { measureCompactPatientHeaderBottom } from "../../helpers/utility";
import useTabHelperLabel, { resolveTabAnchor } from "../../hooks/useTabHelperLabel";
import { getDefaultOpService } from "./OPLandingLayout/opHelpers";
import useClinicalRoleForms from "../../hooks/useClinicalRoleForms";
import { ADMISSION_TYPES } from "../PatientsIP/ClinicalPractitioner/admissionContext";
import "./patientHeader.css";
import useHorizontalScrollAffordance from "../../hooks/useHorizontalScrollAffordance";

const { useToken } = theme;
const COMPACT_TABS_MEDIA = "(max-width: 768px)";
const TABLET_TABS_MEDIA = "(min-width: 769px) and (max-width: 1280px)";

const OpPatientHeader = () => {
  const [isCompactTabs, setIsCompactTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(COMPACT_TABS_MEDIA).matches
      : false
  );
  const [isTabletTabs, setIsTabletTabs] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(TABLET_TABS_MEDIA).matches
      : false
  );
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { token } = useToken();
  const headerRef = useRef(null);
  const tabsScrollRef = useRef(null);

  const role = (getCookie(Role) || "doctor").toLowerCase().replaceAll("_", "-");
  const status = params.get("status") || "today";
  const pid = params.get("pid");
  const aid = params.get("aid");
  const defaultService = getDefaultOpService(role);
  const service = params.get("service") || defaultService;

  const [patientData, setPatientData] = useState(null);
  const [admission, setAdmission] = useState(null);
  const [pastEmrCount, setPastEmrCount] = useState(0);
  const [tabsOpen, setTabsOpen] = useState(true);

  const helperLabelsEnabled = isCompactTabs || isTabletTabs;
  const { showTabLabel, TabHelperLabel } = useTabHelperLabel(helperLabelsEnabled);

  const clinicalForms = useClinicalRoleForms(aid, ADMISSION_TYPES.OP);

  const tabs = useMemo(
    () =>
      getuserRoleTabs(role, service, token, pastEmrCount, {
        compactIcons: isCompactTabs || isTabletTabs,
        hideTooltips: isCompactTabs || isTabletTabs,
        clinicalForms,
      }),
    [role, service, token, pastEmrCount, isCompactTabs, isTabletTabs, clinicalForms]
  );

  const getTabTitle = (key) => {
    const tab = tabs.find((item) => item.key === key);
    return tab?.tabTitle || tab?.title || key;
  };

  const navigateToService = (key) => {
    params.set("service", key);
    params.delete("start");
    params.delete("type");
    navigate(`?${params.toString()}`, { replace: true });
  };

  const revealTabLabel = (key, domEventOrAnchor) => {
    if (!helperLabelsEnabled) return;
    const title = getTabTitle(key);
    const anchor = resolveTabAnchor(domEventOrAnchor);
    if (!title || !anchor) return;
    window.requestAnimationFrame(() => {
      showTabLabel(key, title, anchor);
    });
  };

  const handleMenuPointerUp = (e) => {
    if (!helperLabelsEnabled || isCompactTabs) return;
    const menuItem = e.target.closest?.(".ant-menu-item");
    if (!menuItem) return;
    const menuRoot = menuItem.closest(".ant-menu");
    const menuItems = menuRoot?.querySelectorAll(".ant-menu-item");
    if (!menuItems?.length) return;
    const index = Array.from(menuItems).indexOf(menuItem);
    const tab = tabs[index];
    if (!tab) return;
    revealTabLabel(tab.key, menuItem);
  };

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_TABS_MEDIA);
    const onChange = () => setIsCompactTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(TABLET_TABS_MEDIA);
    const onChange = () => setIsTabletTabs(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useHorizontalScrollAffordance(tabsScrollRef, [
    service,
    tabsOpen,
    isCompactTabs,
    tabs.length,
  ]);

  
  useEffect(() => {
    const container = tabsScrollRef.current;
    if (!container || !tabsOpen) return;

    window.requestAnimationFrame(() => {
      const activeItem =
        container.querySelector(".mobile-tab-item.active") ||
        container.querySelector(".ant-menu-item-selected");

      if (!activeItem) {
        container.scrollLeft = 0;
        return;
      }

      const cRect = container.getBoundingClientRect();
      const iRect = activeItem.getBoundingClientRect();
      const fullyVisible =
        iRect.left >= cRect.left - 2 && iRect.right <= cRect.right + 2;

      if (fullyVisible) return;

      if (iRect.left < cRect.left) {
        container.scrollLeft += iRect.left - cRect.left - 8;
      } else if (iRect.right > cRect.right) {
        container.scrollLeft += iRect.right - cRect.right + 8;
      }
    });
  }, [service, isCompactTabs, tabsOpen, tabs.length]);

  useEffect(() => {
    if (!pid || !aid) return;
    getOpHistoryAPI(pid, aid).then((res) => {
      const data = res?.data?.data;
      if (!data) return;
      setPatientData(data.patient_data);
      setAdmission(data.admission);
      setPastEmrCount(data?.past_emrs?.length || 0);
    });
  }, [pid, aid]);



  useEffect(() => {
    if (clinicalForms.loading) return;
    if (!tabs.length) return;
    const allowedKeys = new Set(
      tabs.flatMap((t) => [t.key, ...(t.children || []).map((c) => c.key)])
    );
    if (allowedKeys.has(service) || service === "start") return;
    const fallback = tabs[0]?.key || defaultService;
    params.set("service", fallback);
    navigate(`?${params.toString()}`, { replace: true });
  }, [tabs, service, defaultService, navigate, params, clinicalForms.loading]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;

    const setHeaderHeightVar = () => {
      const bottom = measureCompactPatientHeaderBottom(el);
      document.documentElement.style.setProperty(
        "--op-patient-header-height",
        `${bottom}px`
      );
    };

    setHeaderHeightVar();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", setHeaderHeightVar);
      return () => {
        window.removeEventListener("resize", setHeaderHeightVar);
        document.documentElement.style.removeProperty("--op-patient-header-height");
      };
    }

    const ro = new ResizeObserver(() => setHeaderHeightVar());
    ro.observe(el);
    window.addEventListener("resize", setHeaderHeightVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setHeaderHeightVar);
      document.documentElement.style.removeProperty("--op-patient-header-height");
    };
  }, [tabsOpen, patientData, admission, pastEmrCount, service, isCompactTabs, isTabletTabs]);

  useEffect(() => {
    const collapsed = isCompactTabs && !tabsOpen;
    document.documentElement.classList.toggle(
      "ip-patient-header-collapsed",
      collapsed
    );
    return () => {
      document.documentElement.classList.remove("ip-patient-header-collapsed");
    };
  }, [isCompactTabs, tabsOpen]);

  if (!patientData) return null;

  const patientFullName = `${patientData.first_name || ""} ${
    patientData.last_name || ""
  }`.trim();

  return (
    <>
      <Row
        ref={headerRef}
        className={`patient-menu-wrapper ${
          isCompactTabs ? "patient-menu-wrapper--compact-tabs" : ""
        } ${
          isTabletTabs && !isCompactTabs
            ? "patient-menu-wrapper--tablet-tabs"
            : ""
        } ${
          isCompactTabs && !tabsOpen ? "patient-menu-wrapper--tabs-collapsed" : ""
        }`}
        gutter={
          isCompactTabs
            ? [12, 0]
            : isTabletTabs && !isCompactTabs
            ? [0, 6]
            : [12, 12]
        }
      >
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={6}
          xl={6}
          flex={isTabletTabs && !isCompactTabs ? "0 1 auto" : undefined}
          className="patient-menu-info-col"
        >
          <div style={{ marginLeft: 8, marginTop: 2 }}>
            <Space align="center" style={{ width: "100%" }}>
              <LeftOutlined
                onClick={() => {
                  const headerBackEvent = new CustomEvent("opPatientHeaderBack", {
                    cancelable: true,
                  });
                  if (!window.dispatchEvent(headerBackEvent)) {
                    return;
                  }

                  navigate(
                    `/${role}/home?patient-type=outPatients&status=${status}`
                  );
                }}
                style={{ fontSize: 18, cursor: "pointer", marginTop: "6px" }}
              />

              {isCompactTabs ? (
                <Typography.Text
                  className="patient-name"
                  onClick={(e) => {
                    if (patientFullName) {
                      showTabLabel("patient-name", patientFullName, e.currentTarget);
                    }
                  }}
                  style={{
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.25,
                    cursor: "pointer",
                  }}
                >
                  {patientFullName}
                </Typography.Text>
              ) : (
                <Typography.Text className="patient-name">
                  {patientFullName}
                </Typography.Text>
              )}
            </Space>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "4px",
                marginLeft: "26px",
              }}
            >
              <Tag
                style={{
                  fontSize: 12,
                  padding: "2px 10px",
                  backgroundColor: "#ffffffff",
                  color: "#000",
                }}
              >
                {formatPatientAge(patientData.age_by_dob, patientData.gender)}
              </Tag>
              <Tag
                style={{
                  fontSize: 12,
                  padding: "2px 10px",
                  backgroundColor: "#ffffffff",
                  color: "#000",
                }}
              >
                OP ID: {admission?.op_id}
              </Tag>
            </div>

            {isCompactTabs && !tabsOpen && (
              <div className="op-patient-tabs-toggle-row ip-patient-tabs-toggle-row ip-patient-tabs-toggle-row--collapsed">
                <Button
                  type="text"
                  className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                  style={{ color: token.colorPrimary }}
                  aria-label="Expand patient actions"
                  onClick={() => setTabsOpen(true)}
                  icon={<DownOutlined />}
                />
              </div>
            )}
          </div>
        </Col>

        {(!isCompactTabs || tabsOpen) && (
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={18}
          xl={18}
          flex={isTabletTabs && !isCompactTabs ? "1 1 0" : undefined}
          className="patient-menu-tabs-col"
        >
          {isCompactTabs ? (
            <>
              {tabsOpen ? (
                <div className="mobile-tab-scroll ip-compact-patient-tabs">
                  {tabs.map((tab) => {
                    if (tab.children?.length) {
                      const isActive = (tab.children || []).some(
                        (c) => c.key === service
                      );
                      return (
                        <Dropdown
                          key={tab.key}
                          trigger={["click"]}
                          placement="bottom"
                          menu={{
                            selectedKeys: [service],
                            onClick: ({ key }) => navigateToService(key),
                            items: tab.children.map((c) => ({
                              key: c.key,
                              label: c.label,
                            })),
                          }}
                        >
                          <div
                            className={`mobile-tab-item mobile-tab-item--text ${
                              isActive ? "active" : ""
                            }`}
                          >
                            {tab.label}
                          </div>
                        </Dropdown>
                      );
                    }
                    return (
                      <div
                        key={tab.key}
                        className={`mobile-tab-item ${
                          service === tab.key ? "active" : ""
                        }`}
                        onClick={(e) => {
                          showTabLabel(
                            tab.key,
                            getTabTitle(tab.key),
                            e.currentTarget
                          );
                          navigateToService(tab.key);
                        }}
                      >
                        {tab.label}
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <div className="op-patient-tabs-toggle-row">
                <Button
                  type="text"
                  className="op-patient-tabs-toggle op-patient-tabs-toggle--bottom"
                  style={{ color: token.colorPrimary }}
                  aria-label={
                    tabsOpen ? "Collapse patient actions" : "Expand patient actions"
                  }
                  onClick={() => setTabsOpen((v) => !v)}
                  icon={tabsOpen ? <DownOutlined /> : <UpOutlined />}
                />
              </div>
            </>
          ) : (
            <div
              ref={tabsScrollRef}
              className="patient-menu-scroll"
              onPointerUp={handleMenuPointerUp}
            >
              <Menu
                className="patientMenu"
                mode="horizontal"
                selectedKeys={[service]}
                items={tabs}
                disabledOverflow
                style={{ justifyContent: "flex-start" }}
                onClick={(e) => {
                  revealTabLabel(e.key, e.domEvent);
                  navigateToService(e.key);
                }}
              />
            </div>
          )}
        </Col>
        )}
      </Row>
      {TabHelperLabel}
    </>
  );
};

export default OpPatientHeader;
